import { Flex, Icon, PopoverBody, PopoverHeader, Text } from '@chakra-ui/react';
import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import { type ReactNode, useState } from 'react';

import {
  ALL_FILTER_DEFINITIONS,
  SUB_FILTER_IDS,
} from '../engine/filterRegistry';
import type { MultiInstanceMeta } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  FilterId,
  type FilterSelectOptions,
  FilterType,
} from '../types/filters';
import { SegmentedFilterFormRender } from './filterForms/SegmentedFilterFormRender';
import { mapSegmentKeyToLabel } from './segments/SegmentSelector';

export const getSubFilterLabelPostfix = (
  filterId: FilterId,
  primaryFilterId?: FilterId
): string | undefined => {
  if (primaryFilterId === FilterId.COMPANY && SUB_FILTER_IDS.has(filterId)) {
    return '(of entire company)';
  }
  return undefined;
};

export const getGroupFilterOptions = (
  filterId: FilterId,
  primaryFilterId?: FilterId
): FilterSelectOptions[] => {
  const filterDef = ALL_FILTER_DEFINITIONS[filterId];
  if (filterDef?.type !== FilterType.GROUP) return [];

  return (filterDef.children || [])
    .map((childId): FilterSelectOptions | null => {
      const childDef = ALL_FILTER_DEFINITIONS[childId];
      if (!childDef) return null;
      return {
        id: childId,
        name: childDef.name,
        labelPostfix: getSubFilterLabelPostfix(childId, primaryFilterId),
      };
    })
    .filter((option): option is FilterSelectOptions => option !== null);
};

interface SubFilterProps {
  filterSelectDropdown: ReactNode;
  filter: Filter | null;
  close: () => void;
  selectedMultiMeta?: MultiInstanceMeta;
  selectedFilterId?: FilterId | 'AI_SEARCH' | null;
}

export const SubFilter = ({
  filterSelectDropdown,
  filter,
  close,
  selectedMultiMeta,
  selectedFilterId,
}: SubFilterProps) => {
  const [segmentStep, setSegmentStep] = useState<number>(0);
  const [selectedSegmentKey, setSelectedSegmentKey] = useState<string | null>(
    null
  );

  const isSegmentStep1 = segmentStep === 1;
  const isSegmentStep2 = segmentStep === 2;

  const segmentLabel = mapSegmentKeyToLabel(selectedSegmentKey);

  const handleSegmentBack = () => {
    if (segmentStep === 2) {
      setSegmentStep(1);
      setSelectedSegmentKey(null);
    } else {
      setSegmentStep(0);
      setSelectedSegmentKey(null);
    }
  };

  return (
    <>
      <PopoverHeader
        marginBottom={2}
        padding={0}
        fontWeight="semibold"
        borderBottomWidth={0}
        display={isSegmentStep1 ? 'none' : 'block'}
      >
        {isSegmentStep2 ? (
          <Flex paddingX={0.5} alignItems="center">
            <Flex
              width="20px"
              height="20px"
              _hover={{
                background: '#2D426A1A',
              }}
              borderRadius="50%"
              justifyContent="center"
              alignItems="center"
            >
              <Icon
                as={IconArrowLeft}
                boxSize={4}
                cursor="pointer"
                onClick={handleSegmentBack}
              />
            </Flex>
            <Text fontSize={12} fontWeight={600} marginLeft={1}>
              {segmentLabel?.toUpperCase()}
            </Text>
          </Flex>
        ) : (
          filterSelectDropdown
        )}
      </PopoverHeader>
      <PopoverBody padding={0}>
        <SegmentedFilterFormRender
          filter={filter}
          close={close}
          selectedMultiMeta={selectedMultiMeta}
          selectedFilterId={selectedFilterId}
          segmentStep={segmentStep}
          setSegmentStep={setSegmentStep}
          setSelectedSegmentKey={setSelectedSegmentKey}
          handleSegmentBack={handleSegmentBack}
        />
      </PopoverBody>
    </>
  );
};
